import {
    IMapInfo,
    GeometryType,
    Inspire,
    ILayerInfo,
    StyleConfig,
    IMapBaseLayer,
} from 'sdi/source';
import { query, queryK } from 'sdi/shape';
import { FetchData, InteractionExtract, withInteraction } from 'sdi/map';
import { none, some, Option } from 'fp-ts/lib/Option';
import { getGlobalInfoFilteredIssues, issueListToLayer } from '../qa';
import { SyntheticLayerInfo } from 'sdi/app';
import { right } from 'fp-ts/lib/Either';
import {
    issueStyleTypology,
    osmPointStyle,
    osmLineStyle,
    osmPolygonStyle,
    issueStylePriority,
    issueStyleIcon,
} from './style';

export const QAMAPNAME = 'qa-map';

export type SymbologySelector = 'priority' | 'typology';

export type LegendSelector = SymbologySelector | 'objects';

export const getSymbology = queryK('map/symbology');

export const getLegendSymbology = queryK('map/legend/symbology');

export const getBaseLayerName = () => query('data/baselayer/select');

const ignGrayBaseLayer: IMapBaseLayer = {
    codename: 'gray-ign',
    name: {
        fr: 'Fond IGN gris',
        nl: 'NGI Gray',
    },
    srs: 'EPSG:3857',
    url: 'https://cartoweb.wms.ngi.be/service',
    params: {
        VERSION: '1.3.0',
        LAYERS: {
            fr: 'topo_grey',
            nl: 'topo_grey',
        },
    },
};

const spwOrthoLastBaseLayer: IMapBaseLayer = {
    codename: 'Ortho',
    name: {
        fr: 'Images aériennes IGN',
        nl: "NGI IGN luchtfoto's",
    },
    srs: 'EPSG:3857',
    url: 'https://wms.ngi.be/inspire/ortho/service',
    params: {
        VERSION: '1.3.0',
        LAYERS: {
            fr: 'orthoimage_coverage',
            nl: 'orthoimage_coverage',
        },
    },
};

const baseLayers: IMapBaseLayer[] = [ignGrayBaseLayer, spwOrthoLastBaseLayer];

export const getBaseLayerNames = () => [...baseLayers.map(c => c.codename)];

export const getBaseLayer = () => []; //TEMP desactivate the loading of base layer below the OSM layer
//     [
//      fromNullable(
//          baseLayers.find(c => c.codename === getBaseLayerName())
//      ).getOrElse(ignGrayBaseLayer),
// ];

export const getView = queryK('port/map/view');

export const OSM_POINT = 'osm-point';
export const OSM_LINE = 'osm-line';
export const OSM_POLY = 'osm-poly';

export const getMapInfo = (): IMapInfo => ({
    baseLayer: 'local/gray',
    id: QAMAPNAME,
    url: `/none`,
    lastModified: Date.now(),
    status: 'published',
    title: { fr: 'QA OSM', nl: 'QA OSM', en: 'QA OSM' },
    description: { fr: 'QA OSM', nl: 'QA OSM', en: 'QA OSM' },
    categories: [],
    attachments: [],
    layers: [
        osmLayerInfo(OSM_POINT, 'Point'),
        osmLayerInfo(OSM_LINE, 'LineString'),
        osmLayerInfo(OSM_POLY, 'Polygon'),
        issueLayerInfo(
            'issues-typology',
            issueStyleTypology(),
            0,
            30,
            getSymbology() === 'typology'
        ),
        issueLayerInfo(
            'issues-priority',
            issueStylePriority(),
            0,
            30,
            getSymbology() === 'priority'
        ),
        issueLayerInfo('issues-object', issueStyleIcon(), 0, 30),
    ],
    id_origin: '',
});

export const getInteraction = queryK('port/map/interaction');

export const withExtract = () => {
    let ret: Option<InteractionExtract> = none;
    withInteraction<InteractionExtract>(
        'extract',
        i => (ret = some(i))
    )(getInteraction());
    return ret;
};

const NOW = new Date().toISOString();

export const metadataTemplate = (
    id: string,
    geometryType: GeometryType
): Inspire => ({
    id,
    geometryType,
    resourceTitle: { fr: '' },
    resourceAbstract: { fr: '' },
    uniqueResourceIdentifier: 'client://',
    topicCategory: [],
    keywords: [],
    geographicBoundingBox: { west: 0.0, north: 0.0, east: 0.0, south: 0.0 },
    temporalReference: { creation: NOW, revision: NOW },
    responsibleOrganisation: [1],
    metadataPointOfContact: [1],
    metadataDate: NOW,
    published: false,
    dataStreamUrl: null,
    maintenanceFrequency: 'unknown',
});

const issueLayerInfo = (
    id: string,
    style: StyleConfig,
    minZoom: number,
    maxZoom: number,
    visible = true
): ILayerInfo => ({
    id,
    style,
    minZoom,
    maxZoom,
    legend: null,
    group: null,
    metadataId: `metadata-${id}`,
    featureViewOptions: { type: 'default' },
    visible,
    visibleLegend: true,
    opacitySelector: false,
});

export const osmLayerInfo = (
    id: string,
    geometryType: GeometryType
): ILayerInfo => ({
    id,
    legend: null,
    group: null,
    metadataId: id,
    featureViewOptions: { type: 'default' },
    visible: true,
    style: (() => {
        switch (geometryType) {
            case 'Point':
            case 'MultiPoint':
                return osmPointStyle();
            case 'LineString':
            case 'MultiLineString':
                return osmLineStyle();
            case 'Polygon':
            case 'MultiPolygon':
                return osmPolygonStyle();
        }
    })(),
    visibleLegend: true,
    opacitySelector: false,
});

export const fetchIssuesData: FetchData = () =>
    right(
        getGlobalInfoFilteredIssues().map(issues => issueListToLayer(issues))
    );

export const getIssuesSynthInfo =
    (id: string, style: StyleConfig) => (): Option<SyntheticLayerInfo> =>
        some({
            info: issueLayerInfo(id, style, 0, 30),
            name: { fr: id },
            metadata: metadataTemplate(`metadata-${id}`, 'Point'),
        });

export const getIssueGeometrySynthInfo =
    (id: string, geometryType: GeometryType) =>
    (): Option<SyntheticLayerInfo> =>
        some({
            info: osmLayerInfo(id, geometryType),
            name: { fr: id },
            metadata: metadataTemplate(id, geometryType),
        });

export const getOsmSynthInfo = (
    id: string,
    geometryType: GeometryType
): Option<SyntheticLayerInfo> =>
    some({
        info: osmLayerInfo(id, geometryType),
        name: { fr: 'osm' },
        metadata: metadataTemplate(id, geometryType),
    });
